import {
  Box,
  DeleteIcon,
  Divider,
  Edit1Icon,
  GKButton,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Show,
  StarFillIcon,
  StarIcon,
  Tab,
  Tabs,
  UploadIcon,
  VectorIcon,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  EquityBasketListInterface,
  SingleModelPortfolioVersionInterface,
} from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import EquityBasketAssets from "../EquityBasketAssets";
import EquityBasketStockRecommedation from "../EquityBasketStockRecommendation";
import ModelPortfolioVersion from "../ModelPortfolioVersion";
import ImportModelPortfolio from "../ModelPortfolioVersion/ImportModelPortfolio";
import RebalanceModal from "../ModelPortfolioVersion/RebalanceModal";
import ViewModelPortfolioVersion from "../ModelPortfolioVersion/ViewModelPortfolioVersion";
import Rebalances from "./Rebalances";

const EquityBasketManage = () => {
  const dispatch = useAppDispatch();

  const { id } = useParams();

  /** ========================================================================================================= */
  const { data } = useFetchQuery({
    key: ["GET_EQUITY_BASKET_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    enabled: Boolean(id),
  });

  const basketDetails: EquityBasketListInterface = data?.data;

  useDocumentTitle(basketDetails?.name || "Equity Basket Manage");

  const { manageEquityBasketTab } = useAppSelector((state) => state?.app?.tab);

  const navigate = useNavigate();

  const [openModelToRebalance, setOpenModelToRebalance] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] =
    useState<HTMLElement | null>(null);

  const [importSheetModal, setImportSheetModal] = useState(false);

  /** ================================================================================== */
  const { mutate: modalPortfolioDelete } = useMutation({
    mutationFn: (versionId: any) =>
      axiosInstance.delete(`/model_portfolio/${id}/version/${versionId}/`),
    onSuccess: () => {
      refetchQuery("GET_ALL_VERSIONS");
    },
  });

  /** ================================================================================== */
  const { mutate: modalPortfolioDefault } = useMutation({
    mutationFn: (versionId: any) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/version/${versionId}/primary/`,
      ),
    onSuccess: () => {
      refetchQuery("GET_ALL_VERSIONS");
    },
  });

  /** ========================================================================================================= */
  const { data: allVersionsList } = useFetchQuery({
    key: ["GET_ALL_VERSIONS"],
    route: `model_portfolio/${id}/versions_list/`,
  });

  const modelPortfolioDetails: SingleModelPortfolioVersionInterface =
    allVersionsList?.data[0];

  /** ========================================================================================================= */
  const handleReturnModelTabElement = () => {
    if (allVersionsList?.data?.length === 0) {
      return (
        <NoData
          additionalComponent={
            <Grid2 container spacing={1}>
              <GKButton
                size="medium"
                variant="outlined"
                color="dark"
                startIcon={<UploadIcon />}
                onClick={() => {
                  setImportSheetModal(true);
                }}
              >
                Import Model Portfolio
              </GKButton>
              <GKButton
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={() => {
                  navigate(`/app/equity-basket/${id}/model-portfolio/add`);
                }}
              >
                Add Model Portfolio
              </GKButton>
            </Grid2>
          }
          text="No Model Portfolio Added Yet"
          subText="Create your first model portfolio to begin tracking and managing investment assets."
        />
      );
    }
    if (basketDetails?.managementStyle === "Subscription") {
      return (
        <ViewModelPortfolioVersion
          selectedModelPortfolio={allVersionsList?.data[0]}
        />
      );
    }
    return <ModelPortfolioVersion />;
  };

  const allTabs = [
    {
      name: "Model Portfolio",
      element: handleReturnModelTabElement(),
      show: basketDetails?.basketType === "Model",
    },
    {
      name: "Rebalances",
      element: <Rebalances />,
      show: basketDetails?.basketType === "Model",
    },
    {
      name: "Stock Recommendations",
      element: <EquityBasketStockRecommedation />,
      show: basketDetails?.basketType === "Asset",
    },
    {
      name: "Assets",
      element: <EquityBasketAssets />,
      show: true,
    },
  ];

  return (
    <DashboardLayout title="Equity Basket Manage" disablePadding>
      <Grid2 container>
        <Grid2 size={12}>
          <Box
            display={"flex"}
            gap={1}
            alignItems={"center"}
            justifyContent={"space-between"}
            px={3}
          >
            <Tabs
              onChange={(e, val) => {
                dispatch(
                  TAB({ tabKey: "manageEquityBasketTab", tabValue: val }),
                );
              }}
              value={manageEquityBasketTab}
            >
              {allTabs
                ?.filter((item) => item?.show)
                ?.map((d, index) => {
                  return <Tab label={d.name} value={index} key={index} />;
                })}
            </Tabs>
            <Box
              display={
                basketDetails?.basketType !== "Asset" &&
                basketDetails?.managementStyle === "Subscription" &&
                manageEquityBasketTab === 0
                  ? "flex"
                  : "none"
              }
              gap={1}
              alignItems={"center"}
            >
              <IconButton
                color="error"
                onClick={(e) => setDeleteConfirmation(e.currentTarget)}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                sx={{ color: "var(--black)" }}
                onClick={() => {
                  modalPortfolioDefault(modelPortfolioDetails?.id);
                }}
                disabled={modelPortfolioDetails?.primary}
              >
                {modelPortfolioDetails?.primary ? (
                  <StarFillIcon />
                ) : (
                  <StarIcon />
                )}
              </IconButton>
              <IconButton
                sx={{ color: "var(--black)" }}
                onClick={() => {
                  navigate(
                    `/app/equity-basket/${id}/model-portfolio/${modelPortfolioDetails?.id}/edit`,
                  );
                }}
              >
                <Edit1Icon />
              </IconButton>
              <Divider orientation="vertical" sx={{ height: "30px" }} />
              <GKButton
                startIcon={<VectorIcon />}
                onClick={() => setOpenModelToRebalance(true)}
              >
                Rebalance
              </GKButton>
            </Box>
          </Box>
          <Divider />
        </Grid2>
        <Grid2 size={12} p={3}>
          {allTabs
            ?.filter((item) => item?.show)
            .map((d, i) => {
              return (
                <Show.When isTrue={manageEquityBasketTab === i}>
                  {d.element}
                </Show.When>
              );
            })}
        </Grid2>
      </Grid2>
      <Show.When isTrue={openModelToRebalance}>
        <RebalanceModal
          setOpen={setOpenModelToRebalance}
          open={openModelToRebalance}
          selectedId={modelPortfolioDetails?.id}
        />
      </Show.When>
      <Show.When isTrue={importSheetModal}>
        <ImportModelPortfolio
          setOpen={setImportSheetModal}
          open={importSheetModal}
          isRebalanceModal={false}
        />
      </Show.When>
      {deleteConfirmation && (
        <PopConfirm
          anchorEl={deleteConfirmation}
          open={Boolean(deleteConfirmation)}
          setOpen={setDeleteConfirmation}
          onSuccess={() => modalPortfolioDelete(modelPortfolioDetails?.id)}
        />
      )}
    </DashboardLayout>
  );
};

export default EquityBasketManage;
